import React from 'react';

// DEPENDENCIES
import { compose } from 'redux';
import { useFormikContext } from 'formik';
import moment from 'moment';

// GLOBAL VARIABLES
import { NEW_USED } from 'codes';
import { INVENTORY_FILE_TYPE_IDS } from 'globals.js';

// GLOBAL FUNCTIONS
import { formatDollar } from 'functions.js';

// LOCAL VARIABLES
import { INVENTORY_SOURCE_IDS } from './variables';

// HELPERS
import { withRouter } from 'helpers/withRouter';
import useRecordView from 'helpers/getRecordView';

// GLOBAL COMPONENTS
import Card from 'parts/card/Card';
import Form from 'core/form/Form';

// LOCAL COMPONENTS
import Attachment from 'routers/interface/tools/attachment/Attachment';
import DmsTooltip from '../routes/sales/tools/DmsTooltip';

// STYLE
import styles from './pricingInfo.module.scss';

// MAIN COMPONENT
const PricingInfoForm = compose(withRouter)(({ location: { search } }) => {
  const { isView } = useRecordView();

  const { values } = useFormikContext() || {};
  const { new_used, is_order, inventory_source_id, dmsinvoice, dms_invoice_price, internet_price, date_internet_price_sync } = values || {};
  const updateOnValue = dmsinvoice ? dmsinvoice.split(': ')[1] : '';
  const internetPriceDate = date_internet_price_sync ? `as of ${moment(date_internet_price_sync).format('MM/DD/YYYY')} at ${moment(date_internet_price_sync).format('h:mm A')}` : `: ${updateOnValue}`;
  const params = new URLSearchParams(search);
  const newUsed = parseInt(params.get('new_used') || new_used);
  const isNew = newUsed === NEW_USED.new.numeric;
  const isOrder = +is_order === 1;
  const isTrade = +inventory_source_id === INVENTORY_SOURCE_IDS.TRADE;
  const dmsCost = `${dms_invoice_price ? formatDollar(dms_invoice_price) : 'Not available'} ${dmsinvoice}`;

  return (
    <Form.Body plaintext={+isView}>
      <Form.Control name="retail_price" label={ isView ? 'Retail' : !isView && isNew ? 'MSRP' : 'Retail Price'} schema="dollars" required={!isOrder && isNew} />
      <Attachment.Upload
        label="MSRP Sticker/Window Sticker"
        name="documentAttachment.msrp"
        documentType={isNew ? INVENTORY_FILE_TYPE_IDS.MSRP_STICKER_NEW : INVENTORY_FILE_TYPE_IDS.MSRP_STICKER_USED}
        required={!isView}
        useTempDocUpr={false}
      />
      {isView
        ? <Form.Control label="Internet:"
                        value={`${formatDollar(internet_price || 0)} ${internetPriceDate}`}
                        suffix={<DmsTooltip />}
                        className={styles.span}
                        text={date_internet_price_sync ? updateOnValue : ''} />
        : <Form.Control name='internet_price'
                        label='Internet Price'
                        schema='dollars' />
      }
      <Form.Control
        name="invoice_cost"
        label={isNew ? 'Factory Invoice Cost' : 'ACV'}
        schema="dollars"
        required={!isOrder}
      />
      <Attachment.Upload
        label={isNew ? 'Factory Invoice' : 'Purchase Invoice'}
        name="documentAttachment.invoice"
        documentType={isNew ?  INVENTORY_FILE_TYPE_IDS.FACTORY_INVOICE_NEW :  INVENTORY_FILE_TYPE_IDS.PURCHASE_INVOICE_USED}
        required={!isOrder && !isTrade}
        useTempDocUpr={false}
      />
      {!isNew &&
        <Attachment.Upload
          label="Purchase Appraisal"
          name="documentAttachment.appraisal"
          documentType={INVENTORY_FILE_TYPE_IDS.PURCHASE_APPRAISAL_USED}
        />
      }
      {isView && <hr />}
      {isView && (
        <Form.Control
          label="DMS Cost"
          fullWidth
          suffix={<DmsTooltip />}
          className={styles.span}
          input={{ title: dmsCost }}
          value={dmsCost}
        />
      )}
    </Form.Body>
  );
});

// EXPORT
export default function PricingInfo(props) {
  return <Card title="Pricing Info" body={<PricingInfoForm {...props} />} />;
}
